import React, { useState } from 'react';
import { collection, addDoc, doc, getDoc, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../contexts/AuthContext';
import {
  DEFAULT_SHARE_TEMPLATE,
  toDayName,
  toGregorianArabic,
  toHijriDate,
  buildShareMessage,
  openWhatsApp,
  buildGoogleCalendarUrl
} from '../../utils/whatsapp';
import './BookingModal.css';

export default function BookingModal({ date, calendarId, calendar, onClose, onSuccess }) {
  const { currentUser } = useAuth();
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form'); // 'form' | 'share'
  const [shareMessage, setShareMessage] = useState('');

  // Get user name if logged in
  React.useEffect(() => {
    async function getUserName() {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setName(userDoc.data().displayName || '');
        }
      }
    }
    getUserName();
  }, [currentUser]);

  // Format date for display
  const arabicMonths = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const dateParts = date.split('-');
  const day = parseInt(dateParts[2], 10);
  const month = arabicMonths[parseInt(dateParts[1], 10) - 1];
  const year = parseInt(dateParts[0], 10);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!name.trim()) {
      return setError('يرجى إدخال الاسم');
    }

    try {
      setLoading(true);
      setError('');

      // Server-side check: verify the date is still available
      const existingQ = query(
        collection(db, 'bookings'),
        where('calendarId', '==', calendarId),
        where('date', '==', date)
      );
      const existingSnap = await getDocs(existingQ);
      if (!existingSnap.empty) {
        setError('عذراً، تم حجز هذا اليوم للتو. يرجى اختيار يوم آخر.');
        setLoading(false);
        return;
      }

      const bookingData = {
        calendarId: calendarId,
        date: date,
        bookedBy: currentUser ? currentUser.uid : null,
        guestName: name.trim(),
        createdAt: new Date().toISOString()
      };

      if (note.trim()) {
        bookingData.note = note.trim();
      }

      if (!currentUser && password.trim()) {
        bookingData.deletePassword = password.trim();
      }

      await addDoc(collection(db, 'bookings'), bookingData);

      // Show share screen for everyone after a successful booking
      if (calendar) {
        const msg = buildShareMessage(calendar.shareTemplate || DEFAULT_SHARE_TEMPLATE, {
          dayName: toDayName(date),
          gregorianDate: toGregorianArabic(date),
          hijriDate: toHijriDate(date),
          note: note
        });
        setShareMessage(msg);
        setStep('share');
      } else {
        onSuccess();
      }
    } catch (err) {
      console.error('Error creating booking:', err);
      setError('حدث خطأ أثناء الحجز. يرجى المحاولة مرة أخرى.');
    } finally {
      setLoading(false);
    }
  }

  // --- Share screen (shown after successful booking for logged-in users) ---
  if (step === 'share') {
    return (
      <div className="modal-overlay" onClick={onSuccess}>
        <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>تم الحجز بنجاح</h2>
            <button className="close-btn" onClick={onSuccess}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="booking-success-header">
            <svg width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
              <polyline points="22 4 12 14.01 9 11.01"></polyline>
            </svg>
            <div>
              <p className="success-name">{name}</p>
              <p className="success-date">{day} {month} {year}</p>
            </div>
          </div>

          <div className="share-section">
            <label className="share-label">شارك الحجز على واتساب</label>
            <textarea
              className="share-message-textarea"
              value={shareMessage}
              onChange={(e) => setShareMessage(e.target.value)}
              rows={7}
              dir="rtl"
            />
            <button
              className="btn-whatsapp"
              onClick={() => openWhatsApp(shareMessage)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              إرسال عبر واتساب
            </button>
            <a
              className="btn-google-calendar"
              href={buildGoogleCalendarUrl({ date, calendarName: calendar.name, guestName: name, note })}
              target="_blank"
              rel="noopener noreferrer"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="16" y1="2" x2="16" y2="6"></line>
                <line x1="8" y1="2" x2="8" y2="6"></line>
                <line x1="3" y1="10" x2="21" y2="10"></line>
              </svg>
              أضف إلى تقويم Google
            </a>
          </div>

          <div className="modal-actions" style={{ marginTop: '12px' }}>
            <button className="btn btn-secondary" style={{ width: '100%' }} onClick={onSuccess}>
              إغلاق
            </button>
          </div>
        </div>
      </div>
    );
  }

  // --- Booking form ---
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>تأكيد الحجز</h2>
          <button className="close-btn" onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="booking-date-display">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="16" y1="2" x2="16" y2="6"></line>
            <line x1="8" y1="2" x2="8" y2="6"></line>
            <line x1="3" y1="10" x2="21" y2="10"></line>
          </svg>
          <div className="date-text">
            <span className="date-day">{day}</span>
            <span className="date-month-year">{month} {year}</span>
          </div>
        </div>

        <p className="booking-description">
          {currentUser
            ? 'يمكنك تعديل الاسم للحجز نيابة عن شخص آخر'
            : 'يرجى إدخال اسمك لإتمام الحجز'}
        </p>

        {error && <div className="error-alert">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">الاسم *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="أدخل الاسم"
              disabled={loading}
              maxLength={50}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="note">المناسبة (اختياري)</label>
            <input
              id="note"
              type="text"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="مثال: اجتماع العائلة"
              disabled={loading}
              maxLength={100}
            />
          </div>

          {!currentUser && (
            <div className="form-group">
              <label htmlFor="password">كلمة مرور الحذف (اختياري)</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="لحماية الحجز من الحذف"
                disabled={loading}
                maxLength={20}
              />
              <small className="form-hint">إذا أضفت كلمة مرور، ستحتاجها لحذف الحجز لاحقاً</small>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              إلغاء
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || !name.trim()}
            >
              {loading ? 'جاري الحجز...' : 'تأكيد الحجز'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
